// Professional issue analysis panel — real-time AI-extracted metadata display
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  FileText, Monitor, Layers, MapPin, Briefcase,
  Clock, AlertCircle, Paperclip,
} from 'lucide-react'
import { IssueClassificationBadge } from './IssueClassificationBadge'
import { cn } from '@/lib/utils'
import type { IssueRecorderOutput } from '@/types/ai'

interface IssueAnalysisPanelProps {
  extractedData: IssueRecorderOutput['extractedData']
  completeness: number
  uploadedFiles?: File[]
  className?: string
}

export function IssueAnalysisPanel({
  extractedData,
  completeness,
  uploadedFiles = [],
  className,
}: IssueAnalysisPanelProps): JSX.Element {
  const hasData = Object.values(extractedData).some((v) => v !== undefined && v !== '')

  return (
    <Card className={cn('border-kaycoo-teal-light bg-kaycoo-white', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base text-kaycoo-teal-dark">
            <FileText className="h-4 w-4" />
            Issue Analysis
          </CardTitle>
          {completeness > 0 && (
            <Badge variant="outline" className="border-kaycoo-teal text-kaycoo-teal-dark">
              {completeness}% complete
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasData ? (
          <EmptyState />
        ) : (
          <>
            <SummarySection data={extractedData} />
            <div className="h-px bg-kaycoo-white-3" />
            <MetadataSection data={extractedData} />
            {uploadedFiles.length > 0 && (
              <>
                <div className="h-px bg-kaycoo-white-3" />
                <AttachmentsSection files={uploadedFiles} />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center">
      <AlertCircle className="h-8 w-8 text-kaycoo-charcoal-2 opacity-40" />
      <p className="text-sm text-kaycoo-charcoal-2">
        Start describing your issue in the chat. Analysis will appear here automatically.
      </p>
    </div>
  )
}

function SummarySection({ data }: { data: IssueRecorderOutput['extractedData'] }): JSX.Element {
  return (
    <div className="space-y-3">
      {data.problemTitle && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-kaycoo-charcoal-2">Issue Title</p>
          <p className="mt-1 text-sm font-semibold text-kaycoo-charcoal">{data.problemTitle}</p>
        </div>
      )}
      {data.problemDescription && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-kaycoo-charcoal-2">Summary</p>
          <p className="mt-1 text-sm text-kaycoo-charcoal leading-relaxed">{data.problemDescription}</p>
        </div>
      )}
      <IssueClassificationBadge category={data.expertiseDomain || undefined} />
    </div>
  )
}

function MetadataSection({ data }: { data: IssueRecorderOutput['extractedData'] }): JSX.Element {
  const items = [
    { icon: Monitor, label: 'Version', value: data.d365Version },
    { icon: Layers, label: 'Product', value: formatProduct(data.d365Product) },
    { icon: Layers, label: 'Module', value: formatModules(data.affectedModules) },
    { icon: MapPin, label: 'Deployment', value: formatDeployment(data.d365DeploymentType) },
    { icon: Briefcase, label: 'Company', value: data.companyName },
    { icon: Clock, label: 'Environment', value: data.environmentName },
  ].filter((item) => item.value)

  if (items.length === 0) return <></>

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-kaycoo-charcoal-2">Metadata</p>
      <div className="grid grid-cols-2 gap-2">
        {items.map(({ icon: Icon, label, value }) => (
          <div key={label} className="rounded-md border border-kaycoo-white-3 bg-kaycoo-white-2 p-2">
            <div className="flex items-center gap-1.5">
              <Icon className="h-3 w-3 text-kaycoo-teal" />
              <span className="text-[10px] font-medium uppercase text-kaycoo-charcoal-2">{label}</span>
            </div>
            <p className="mt-0.5 text-xs font-medium text-kaycoo-charcoal">{value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function AttachmentsSection({ files }: { files: File[] }): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-kaycoo-charcoal-2">
        Attachments ({files.length})
      </p>
      {files.map((file, i) => (
        <div key={i} className="flex items-center gap-2 text-xs text-kaycoo-charcoal">
          <Paperclip className="h-3 w-3 text-kaycoo-teal" />
          <span className="truncate">{file.name}</span>
        </div>
      ))}
    </div>
  )
}

function formatProduct(product?: string): string | undefined {
  if (!product) return undefined
  return product.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatModules(modules?: string[] | string): string | undefined {
  if (!modules) return undefined
  if (typeof modules === 'string') return modules
  if (modules.length === 0) return undefined
  return modules.join(', ')
}

function formatDeployment(type?: string): string | undefined {
  if (!type) return undefined
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
